'use client';

import { useVirtualizer } from '@tanstack/react-virtual';
import { AlertCircle, Download, Loader2, X } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useSession } from 'next-auth/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import type { Regulation } from '@justisio/shared';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useMediaQuery } from '@/hooks/use-media-query';
import { cn } from '@/lib/utils';

// Load the worker from the app's own bundled copy instead of the unpkg CDN.
// The CDN URL is a protocol-relative link that resolves to http:// on localhost and is
// subject to redirects without CORS headers, ad blockers, and network policy, all of
// which break the worker's dynamic import ("Setting up fake worker failed").
// The bundled copy at /static/pdf.worker.min.mjs is a byte-identical build of the
// pdfjs-dist version pinned by react-pdf, so it must be refreshed when that version changes.
pdfjs.GlobalWorkerOptions.workerSrc = '/static/pdf.worker.min.mjs';

/** A PDF source react-pdf understands: a URL or in-memory bytes/blob. */
export type PdfFileSource = string | ArrayBuffer | Blob | null;

export interface PdfViewerContentProps {
  file: PdfFileSource;
  /** Shown in the toolbar; omitted hides the label. */
  fileName?: string;
  onClose?: () => void;
  /** Called when the toolbar's download button is pressed. */
  onDownload?: () => void;
  /**
   * True when the surface hosting the viewer draws its own close button over
   * the toolbar's top-right corner (the mobile sheet does). The toolbar then
   * keeps its trailing controls clear of that corner.
   */
  hostCloseButton?: boolean;
  /**
   * Max rendered page width in px (default 600). The regulation page keeps the
   * default; full-width surfaces (e.g. the draft PDF panel) pass a larger cap
   * so pages don't render tiny in a wide container.
   */
  maxContentWidth?: number;
}

function PdfViewerContent({
  file,
  fileName,
  onClose,
  onDownload,
  hostCloseButton = false,
  maxContentWidth = 600,
}: PdfViewerContentProps) {
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(600);

  const pageGap = 0;

  const estimatedPageHeight = Math.round(containerWidth * 1.414);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const padding = 32;
        const availableWidth = containerRef.current.clientWidth - padding;
        setContainerWidth(Math.min(availableWidth, maxContentWidth));
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, [maxContentWidth]);

  const onDocumentLoadSuccess = useCallback(({ numPages: pageCount }: { numPages: number }) => {
    setNumPages(pageCount);
    setLoading(false);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((err: Error) => {
    setLoading(false);
    setError(err.message || 'Failed to load PDF');
  }, []);

  const totalPages = useMemo(() => Math.max(numPages, 0), [numPages]);

  const virtualizer = useVirtualizer({
    count: totalPages,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimatedPageHeight + pageGap,
    overscan: 5,
  });

  const virtualItems = virtualizer.getVirtualItems();

  return (
    <div ref={containerRef} className="flex flex-col h-full bg-gray-100">
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border-b border-red-200 text-red-700">
          <AlertCircle size={20} />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <PdfViewerToolbar
        fileName={fileName}
        onClose={onClose}
        onDownloadClick={onDownload ?? null}
        hostCloseButton={hostCloseButton}
      />

      <div ref={scrollRef} className="flex-1 overflow-auto p-4">
        {loading && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="flex flex-col items-center gap-3 text-gray-600">
              <Loader2 size={32} className="animate-spin" />
              <span className="text-sm">Memuat PDF...</span>
            </div>
          </div>
        )}

        <div className="flex flex-col items-center">
          <Document
            file={file}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={null}
            error={null}
          >
            {!loading && totalPages > 0 && (
              <div
                className="relative w-full flex flex-col items-center"
                style={{ height: `${virtualizer.getTotalSize()}px` }}
              >
                {virtualItems.map((virtualItem) => {
                  const pageNumber = virtualItem.index + 1;

                  return (
                    <div
                      key={`page_${pageNumber}`}
                      className="absolute left-1/2 -translate-x-1/2 bg-white shadow-lg"
                      style={{
                        transform: `translate(-50%, ${virtualItem.start}px)`,
                        width: `${containerWidth}px`,
                        marginBottom: `${pageGap}px`,
                      }}
                    >
                      <Page
                        pageNumber={pageNumber}
                        width={containerWidth}
                        renderTextLayer
                        renderAnnotationLayer={false}
                        className="shadow-md"
                        loading={
                          <div
                            style={{ width: containerWidth, height: estimatedPageHeight }}
                            className="flex items-center justify-center bg-gray-50"
                          >
                            <Loader2 size={24} className="animate-spin text-gray-400" />
                          </div>
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </Document>
        </div>
      </div>
    </div>
  );
}

function PdfViewerToolbar({
  fileName,
  onClose,
  onDownloadClick,
  hostCloseButton,
}: {
  fileName?: string;
  onClose?: () => void;
  onDownloadClick: (() => void) | null;
  hostCloseButton: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200 shrink-0',
        // The host's close button sits 16px from the right edge and is 16px
        // wide; this leaves the download button a clear gap to its left.
        hostCloseButton && 'pr-14',
      )}
    >
      {onClose ? (
        <button
          onClick={onClose}
          className="p-2 hover:bg-gray-100 rounded-md transition-colors border border-transparent hover:border-black"
          title="Tutup PDF"
        >
          <X size={20} />
        </button>
      ) : (
        <div />
      )}

      {fileName && (
        <span className="flex-1 min-w-0 px-3 text-sm font-medium text-gray-700 truncate text-center">
          {fileName}
        </span>
      )}

      {onDownloadClick ? (
        <button
          onClick={onDownloadClick}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold text-gray-600 hover:text-black hover:bg-gray-100 rounded-md transition-colors"
          title="Unduh PDF"
        >
          <Download size={18} />
          <span className="hidden sm:inline">Unduh</span>
        </button>
      ) : (
        <div />
      )}
    </div>
  );
}

interface RegulationPdfProps {
  regulation: Regulation;
  onClose?: () => void;
  hostCloseButton?: boolean;
}

/** Adapter that maps a stored regulation to its served PDF (download included). */
function RegulationPdfContent({ regulation, onClose, hostCloseButton }: RegulationPdfProps) {
  const { data: session } = useSession();
  const router = useRouter();

  const handleDownload = () => {
    if (!session?.user) {
      const currentPath = window.location.pathname;
      router.push(`/login?callbackUrl=${encodeURIComponent(currentPath)}`);
      return;
    }

    const link = document.createElement('a');
    link.href = `/api/regulation/${regulation.regulationId}/pdf?download=true`;
    link.download = '';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <PdfViewerContent
      file={`/api/regulation/${regulation.regulationId}/pdf`}
      fileName={regulation.regulationTitle ?? undefined}
      onClose={onClose}
      onDownload={handleDownload}
      hostCloseButton={hostCloseButton}
    />
  );
}

interface PdfViewerProps {
  regulation: Regulation;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PdfSheet({ regulation, isOpen, onOpenChange }: PdfViewerProps) {
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const showSheet = isOpen && !isDesktop;

  return (
    <Sheet open={showSheet} onOpenChange={onOpenChange}>
      <SheetContent className="w-[90vw] sm:max-w-[800px] p-0 overflow-hidden lg:hidden">
        <div className="h-full">
          <RegulationPdfContent regulation={regulation} hostCloseButton />
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function PdfPanel({
  regulation,
  onClose,
}: {
  regulation: Regulation;
  onClose?: () => void;
}) {
  return (
    <div className="h-full bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] flex flex-col">
      <RegulationPdfContent regulation={regulation} onClose={onClose} />
    </div>
  );
}

export { PdfViewerContent };